/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs';
import { RestClientService } from '../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../shared/store';
import {
  AnalyticsCourse,
  CourseActivityOverview,
  CourseAggregateMetrics,
  CourseGrade,
  StudentCourseMetrics,
} from '../models/analytics.model';
import { AnalyticsDataService } from './analytics-data.service';

@Injectable()
export class ApiAnalyticsDataService implements AnalyticsDataService {
  private readonly analyticsUrl = Location.joinWithSlash(environment.apiRootUrl || '', '/lti/canvas/analytics');

  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getCourses(): ObservableResult<AnalyticsCourse[]> {
    return this.client.get<AnalyticsCourse[]>(`${this.analyticsUrl}/courses`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.myHub.analytics.errors.loadCourses'))),
    );
  }

  getCourseGrades(courseId: number): ObservableResult<CourseGrade[]> {
    return this.client.get<CourseGrade[]>(`${this.analyticsUrl}/courses/${courseId}/grades`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.myHub.analytics.errors.loadCourseDetail'))),
    );
  }

  getCourseMetrics(courseId: number): ObservableResult<CourseAggregateMetrics> {
    return this.client.get<CourseAggregateMetrics>(`${this.analyticsUrl}/courses/${courseId}/metrics`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.myHub.analytics.errors.loadCourseDetail'))),
    );
  }

  getCourseActivityOverview(courseId: number): ObservableResult<CourseActivityOverview> {
    return this.client.get<CourseActivityOverview>(`${this.analyticsUrl}/courses/${courseId}/activity-overview`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() =>
        ObservableResult.ofError(this.translocoService.translate('translations.myHub.analytics.errors.loadActivityOverview')),
      ),
    );
  }

  getMyMetrics(courseId: number): ObservableResult<StudentCourseMetrics> {
    return this.client.get<StudentCourseMetrics>(`${this.analyticsUrl}/courses/${courseId}/my-metrics`).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.myHub.analytics.errors.loadMyMetrics'))),
    );
  }
}
